import { PaymentRequestState, useRepositoryQuery } from '@mews-commander/data-access';
import { Divider, Stack } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Loader } from '@mews-commander/ui';
import { isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { useCommanderSelector } from '@mews-commander/utils';
import { getOrderedServiceOrdersById } from '../../../selectors';
import { PaymentRequestItem } from './PaymentRequestItem';

interface OwnProps {
    serviceOrderId?: string;
    billId: string;
    accountId: string;
    depth?: number;
}

export const PaymentRequestList = ({ accountId, billId, serviceOrderId, depth = 0 }: OwnProps) => {
    const isCompany = useCommanderSelector((state) => isAccountIdCompany(state, { accountId }));
    const realBillId = useCommanderSelector((state) => (isBillVirtual(state, { id: billId }) ? null : billId));
    const billServiceOrderIds = useCommanderSelector((state) => getOrderedServiceOrdersById(state, { billId }));
    const { data: paymentRequests, isLoading } = useRepositoryQuery(
        'paymentRequests',
        'getAll',
        { accountIds: [accountId], state: PaymentRequestState.Pending },
        {
            select: (data) => {
                // This ensures that only payment requests in 'Pending' state are displayed as 'WithTheBank' state can be treated as Pending
                const pendingPaymentRequests = data.paymentRequests?.filter(
                    (pr) => pr.state === PaymentRequestState.Pending
                );
                if (serviceOrderId) {
                    return pendingPaymentRequests?.filter(
                        (pr) => serviceOrderId === pr.serviceOrderId && (!realBillId || realBillId === pr.billId)
                    );
                }

                return pendingPaymentRequests?.filter(
                    (pr) =>
                        (!pr.serviceOrderId || !billServiceOrderIds.includes(pr.serviceOrderId)) &&
                        realBillId === pr.billId
                );
            },
            enabled: !isCompany,
        }
    );

    if (isLoading) {
        // seperate loader because the page itself fetches item differenly
        // using suspense query breaks the page
        return <Loader />;
    }

    if (!paymentRequests || !paymentRequests.length) {
        return null;
    }

    return (
        <Stack vertical>
            {serviceOrderId ? null : (
                <Divider>
                    <LocalizedText textKey={TextKey.PaymentRequestPlural} />
                </Divider>
            )}
            {paymentRequests.map((paymentRequest) => (
                <PaymentRequestItem
                    paymentRequest={paymentRequest}
                    billId={billId}
                    accountId={accountId}
                    depth={depth}
                />
            ))}
        </Stack>
    );
};
